import { ActionIcon, Button, Divider, Grid, Group, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import { ChangeEvent } from 'react';

import { Declaration, DeclarationGoods, DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { EucdmCode, HideElementFor, MultiLangCodes, NumberInput, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { DeclarationViewGoodHVFormValues } from './validateDeclarationViewGoodHVForm';

interface DeclarationViewGoodHVTaxesProps {
  form: UseFormReturnType<DeclarationViewGoodHVFormValues>;
  goodDetails: DeclarationGoods;
  declaration: Declaration;
  editDisabled?: boolean;
  handleTax100: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSupplementaryTotalQuantity: () => string | number | undefined;
  handleTaricSupplementaryUnits: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const DeclarationViewGoodHVTaxes: React.FC<DeclarationViewGoodHVTaxesProps> = ({
  form,
  goodDetails,
  declaration,
  editDisabled,
  handleTax100,
  handleSupplementaryTotalQuantity,
  handleTaricSupplementaryUnits,
}) => {
  const { data: settings } = useSettings();
  const isNetherlands = settings?.country === EnvironmentCountries.NL;
  const codelistSystems = useCodeListSystem({
    group: declaration?.generalInfo?.group,
    isExport: declaration?.generalInfo?.isExport,
  });
  const disableOverruleTotalQuantity = ['CT', 'PX', 'PK'].includes(goodDetails.packaging.typeOfPackages || '');
  const disableOverruleNumberOfPackages = ['PC'].includes(goodDetails.packaging.typeOfPackages || '');
  const declarationGroup = declaration?.generalInfo?.group as DeclarationHighValueGroupType;

  return (
    <Stack mx="xl">
      <Stack mb="xl">
        <Title order={5} fw={600}>
          Overrule total quantity or number of packages to declare
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <NumberInput
              {...form.getInputProps('goodShipmentGlobal.totalQuantity')}
              label="Overrule Total Quantity"
              disabled={editDisabled || disableOverruleTotalQuantity}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.totalNumberOfPackages">
              <NumberInput
                {...form.getInputProps('goodShipmentGlobal.totalNumberOfPackages')}
                label="Overrule Total Number Of Packages"
                disabled={editDisabled || disableOverruleNumberOfPackages}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>

      {!isNetherlands && (
        <>
          <Stack mb="xl">
            <Title order={5} fw={600}>
              Tax 100
            </Title>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  {...form.getInputProps('declarationGoods.dutiesAndTaxes')}
                  disabled={editDisabled}
                  label="Unit code"
                  value={goodDetails.dutiesAndTaxes?.items?.find((item) => item.taxType === '100')?.taxBase?.[0]?.measurementUnitAndQualifier}
                  onChange={handleTax100}
                />
              </Grid.Col>
            </Grid>
          </Stack>

          <Divider mb="md" />
        </>
      )}

      <Divider mb="md" />

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Item amount invoiced
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.itemAmountInvoiced">
              <TextInput label="Item amount invoiced" value={goodDetails.itemAmountInvoiced || 0} disabled />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>

      <Divider mb="md" />

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Total Amount invoiced
        </Title>

        <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
          <Grid>
            <Grid.Col span={6}>
              <NumberInput {...form.getInputProps('goodShipmentGlobal.totalAmountInvoiced')} label="Overrule Total Amount Invoiced" disabled={editDisabled} />
            </Grid.Col>
          </Grid>
        </HideElementFor>
        <Grid>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
            <Grid.Col span={6}>
              <EucdmCode path="goodShipmentGlobal.invoiceCurrency">
                <TextInput label="Invoice currency" value={declaration.goodShipmentGlobal.invoiceCurrency} disabled />
              </EucdmCode>
            </Grid.Col>
          </HideElementFor>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.exchangeRate">
              <NumberInput label="Exchange rate" value={declaration.goodShipmentGlobal.exchangeRate} disabled />
            </EucdmCode>
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col span={6}>
            <NumberInput label="Calculated customs value" value={goodDetails.dutiesAndTaxes?.items?.[0]?.taxBase?.[0]?.amount} disabled />
          </Grid.Col>
        </Grid>
      </Stack>

      <Divider mb="md" />

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Total Mass
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <EucdmCode path="goodsShipmentItems.goodsMeasure.grossMass">
              <NumberInput {...form.getInputProps('goodShipmentGlobal.totalGrossMass')} label="Overrule Total Gross Mass" disabled={editDisabled} />
            </EucdmCode>
          </Grid.Col>
          <HideElementFor condition={() => declarationGroup === DeclarationHighValueGroupType.H2 && settings.country === EnvironmentCountries.NL}>
            <Grid.Col span={6}>
              <NumberInput {...form.getInputProps('goodShipmentGlobal.totalNetMass')} label="Overrule Total Net Mass" disabled={editDisabled} />
            </Grid.Col>
          </HideElementFor>
        </Grid>
      </Stack>

      <Divider mb="md" />

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Supplementary Units
        </Title>

        <Grid>
          <Grid.Col span={6}>
            <TextInput
              {...form.getInputProps('declarationGoods.evalMethodAiHsCode.codeQualifyer')}
              label="Units code"
              disabled={editDisabled}
              onChange={handleTaricSupplementaryUnits}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <EucdmCode path="goodShipmentGlobal.totalQuantitySupplementaryUnits">
              <NumberInput label="Total quantity in Units" value={handleSupplementaryTotalQuantity()} disabled />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <NumberInput
              {...form.getInputProps('goodShipmentGlobal.totalQuantitySupplementaryUnits')}
              label="Overrule total quantity in Units"
              disabled={editDisabled}
            />
          </Grid.Col>
        </Grid>
      </Stack>

      <Divider mb="md" />

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Origin
        </Title>

        <Grid>
          <Grid.Col span={3}>
            <EucdmCode path="consignment.countryOfOrigin">
              <MultiLangCodes
                {...form.getInputProps('consignment.countryOfOrigin')}
                label="Country of Origin"
                code={declaration.generalInfo.isExport ? 'CL008' : 'CL199'}
                required={settings?.country === EnvironmentCountries.NL}
                disabled={editDisabled}
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
          {!isNetherlands && (
            <Grid.Col span={4.5}>
              <EucdmCode path="consignment.regionOfOrigin">
                <TextInput {...form.getInputProps('consignment.regionOfOrigin')} label="Region of Origin" disabled={editDisabled} />
              </EucdmCode>
            </Grid.Col>
          )}
          <Grid.Col span={4.5}>
            <EucdmCode path="consignment.countryOfPreferentialOrigin">
              <MultiLangCodes
                {...form.getInputProps('consignment.countryOfPreferentialOrigin')}
                label="Country of Preferential Origin"
                code={declaration.generalInfo.isExport ? 'CL008' : 'CL199'}
                required={settings?.country === EnvironmentCountries.NL && ['2', '3'].includes(declaration.goodShipmentGlobal?.preference?.[0])}
                disabled={editDisabled}
                system={codelistSystems}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>

      {declaration.generalInfo.isExport && (
        <>
          <Divider mb="md" />

          <Stack mb="xl" align="flex-start">
            <Title order={5} fw={600}>
              Dangerous goods
            </Title>

            {form.values.declarationGoods.dangerousGoods.UNDG.map((_, index) => (
              <Group key={index} align="center" gap="sm" wrap="nowrap">
                <EucdmCode path="goodsShipmentItems.dangerousGoods.UNDG">
                  <TextInput {...form.getInputProps(`declarationGoods.dangerousGoods.UNDG.${index}`)} label="UNDG code" disabled={editDisabled} />
                </EucdmCode>
                {!editDisabled && (
                  <ActionIcon variant="subtle" size="lg" onClick={() => form.removeListItem('declarationGoods.dangerousGoods.UNDG', index)}>
                    <IconTrash size={18} />
                  </ActionIcon>
                )}
              </Group>
            ))}

            {!editDisabled && (
              <Button variant="outline" onClick={() => form.insertListItem('declarationGoods.dangerousGoods.UNDG', '')}>
                Add field for UNDG code
              </Button>
            )}
          </Stack>
        </>
      )}

      <Divider mb="md" />

      <Stack mb="xl">
        <Title order={5} fw={600}>
          Web URL
        </Title>

        <Grid>
          <Grid.Col span={12}>
            <TextInput label="Web URL" value={goodDetails.webScraping.webUrl} disabled />
          </Grid.Col>
        </Grid>
      </Stack>

      <Divider mb="md" />
    </Stack>
  );
};
